import type { ReactNode } from 'react';
import { createElement } from 'react';

export const isJsonString = (str: string) => {
  try {
    JSON.parse(str);
    return true;
  } catch {
    return false;
  }
};

// Helper to unescape HTML entities
export const unescapeHtmlEntities = (str: string) => {
  if (!str || typeof str !== 'string') {
    return '';
  }
  return str
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'"); // Handles apostrophes
};

const parseAllowedHtml = (text: string): ReactNode[] => {
  const nodes: ReactNode[] = [];
  if (!text) {
    return nodes;
  }

  const regex = new RegExp(`</?(${['strong'].join('|')})\\b[^>]*>`, 'gi');
  const activeTags: string[] = [];
  let lastIndex = 0;
  let keyCounter = 0;
  let match: RegExpExecArray | null = null;

  const pushSegment = (segment: string) => {
    if (!segment) {
      return;
    }

    let node: ReactNode = segment;
    for (let index = activeTags.length - 1; index >= 0; index -= 1) {
      const tag = activeTags[index];
      if (tag === 'strong') {
        node = createElement('strong', { key: `strong-${keyCounter++}` }, node);
      }
    }

    nodes.push(node);
  };

  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      pushSegment(text.slice(lastIndex, match.index));
    }

    const tag = match[1]?.toLowerCase();
    const isClosing = match[0].startsWith('</');
    if (tag) {
      if (isClosing) {
        const removeIndex = activeTags.lastIndexOf(tag);
        if (removeIndex !== -1) {
          activeTags.splice(removeIndex, 1);
        }
      } else {
        activeTags.push(tag);
      }
    }

    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < text.length) {
    pushSegment(text.slice(lastIndex));
  }

  return nodes;
};

export const renderRichText = (text?: string): ReactNode => {
  if (!text) {
    return null;
  }

  const decoded = unescapeHtmlEntities(text);
  const nodes = parseAllowedHtml(decoded);
  return nodes.length > 0 ? nodes : decoded;
};
